import type { IncomingMessage, ServerResponse } from 'node:http';
import type { ParsedUrlQuery } from 'node:querystring';

export type Json = string | number | boolean | null | Json[] | { [key: string]: Json };

export type ValueOf<T> = T[keyof T];

export type LiteralUnion<T extends string | number> = T | Omit<T, T>;

export type PreviewData = string | false | object | undefined;

export type IContextBaseParams = { market: string, locale: string };

export type IContextParams = IContextBaseParams & { id: string };

export type StaticPropsContext<
  Q extends ParsedUrlQuery = ParsedUrlQuery,
  D extends PreviewData = PreviewData
> = {
  params: Q;
  draftMode?: boolean;
  locale?: string;
  locales?: string[];
  defaultLocale?: string;
  revalidateReason?: 'build' | 'stale' | 'on-demand';
  preview?: boolean; // deprecated in favor of draftMode
  previewData?: D; // deprecated in favor of draftMode
};

export type IStaticContext<T extends {} = {}> = StaticPropsContext<IContextParams & T>;

export declare type NextApiRequestCookies = Partial<{
  [key: string]: string;
}>;

export type ServerSidePropsContext<
  Q extends ParsedUrlQuery = ParsedUrlQuery,
  D extends PreviewData = PreviewData
> = {
  req: IncomingMessage & {
    cookies: NextApiRequestCookies
  }
  res: ServerResponse;
  params: Q;
  draftMode?: boolean;
  query: ParsedUrlQuery;
  resolvedUrl: string;
  locale?: string;
  locales?: string[];
  defaultLocale?: string;
  revalidateReason?: 'build' | 'stale' | 'on-demand';
  preview?: boolean; // deprecated in favor of draftMode
  previewData?: D; // deprecated in favor of draftMode
};

export type IServerSideSplatContext<T extends {} = {}> = ServerSidePropsContext<IContextBaseParams & T>;

export type IServerSideContext<T extends {} = {}> = ServerSidePropsContext<IContextParams & T>;

export type IPartial<T> = {
  [P in keyof T]?: Partial<T[P]>;
};

export type DeepPartial<T> = T extends object ? {
  [P in keyof T]?: DeepPartial<T[P]>;
} : T;

export type IPrettify<T> = {
  [K in keyof T]: T[K];
} & {};

export type IOverrideReturnValue<T, FromType, ToType> = T extends Record<string, unknown> ? IPrettify<IOverride<T, FromType, ToType>> : T;

export type IOverrideValue<T, FromType, ToType> = T extends FromType ?
  (keyof T extends keyof FromType ? ToType : IOverrideReturnValue<T, FromType, ToType>) :
  IOverrideReturnValue<T, FromType, ToType>;

export type IOverride<T, FromType, ToType> = {
  [Property in keyof T]: IOverrideValue<T[Property], FromType, ToType>;
};

export type RequireAtLeastOne<T> = {
  [K in keyof T]-?: Required<Pick<T, K>> & Partial<Pick<T, Exclude<keyof T, K>>>
}[keyof T];

export type Shift<T extends any[]> = ((...t: T) => any) extends ((
  first: any,
  ...rest: infer Rest
) => any)
  ? Rest
  : never;

export type ShiftUnion<P extends PropertyKey, T extends any[]> = T extends any[]
  ? T[0] extends P ? Shift<T> : never
  : never;

export type DeepRequiredProps<T, P extends string[]> = T extends object
  ? (Omit<T, Extract<keyof T, P[0]>> &
    Required<
      {
        [K in Extract<keyof T, P[0]>]: NonNullable<
          DeepRequiredProps<T[K], ShiftUnion<K, P>>
        >
      }
    >)
  : T;

export type DeepRequired<T> = {
  [K in keyof T]: Required<DeepRequired<T[K]>>
}

/*
export type AtLeastOne<T> = { [K in keyof T]: Pick<T, K> }[keyof T];
*/

/*
export type FlatObject<T> = T extends infer TResult ? { [key in keyof TResult]: TResult[key] } : never;
*/
